import React from 'react';
import { useTranslation } from '../../../../hooks';
import styles from './About.module.css';

/**
 * About Component (Landing Feature)
 * 
 * About section component for the landing page featuring:
 * - Introduction to the platform
 * - Key features grid with icons
 * - Official approval badge
 * - Staggered animations for feature cards
 * - CSS Modules for scoped styling
 * - Responsive design
 * 
 * @component
 * @example
 * return (
 *   <About />
 * )
 */
const About = () => {
  const { t, language } = useTranslation();
  
  const features = [
    { key: 'news', icon: '📰' },
    { key: 'bills', icon: '💳' },
    { key: 'notifications', icon: '📢' },
    { key: 'emergency', icon: '📞' },
    { key: 'council', icon: '🧑‍💼' },
    { key: 'contact', icon: '✉️' },
    { key: 'faq', icon: '❓' }
  ];

  return (
    <section 
      className={styles.about} 
      id="about" 
      aria-labelledby="about-title"
    >
      <div className={styles.container}>
        {/* Header Section */}
        <div className={styles.header}>
          <h2 id="about-title" className={styles.title}>
            {t.about.title}
          </h2>
          
          <p className={styles.description}>
            {t.about.description}
          </p>
          
          <div className={styles.officialBadge}>
            <p className={styles.officialText}>
              {t.about.officiallyApproved}
            </p>
            <p className={styles.licenseNote}>
              {t.about.licenseNote}
            </p>
          </div>
        </div>
        
        {/* Features Section */}
        <div className={styles.featuresSection}>
          <h3 className={styles.featuresTitle}>
            {t.about.keyFeatures}
          </h3>
        </div>
        
        {/* Features Grid */}
        <div className={styles.featuresGrid} role="list">
          {features.map((feature, index) => (
            <div
              key={feature.key}
              className={styles.featureCard}
              role="listitem"
              tabIndex="0"
              aria-labelledby={`feature-${feature.key}-title`}
              aria-describedby={`feature-${feature.key}-desc`}
            >
              <span 
                className={styles.featureIcon}
                role="img" 
                aria-label={t.about.features[feature.key].title}
              >
                {feature.icon}
              </span>
              
              <h4 
                id={`feature-${feature.key}-title`}
                className={styles.featureTitle}
              >
                {t.about.features[feature.key].title}
              </h4>
              
              <p 
                id={`feature-${feature.key}-desc`}
                className={styles.featureDescription}
              >
                {t.about.features[feature.key].description}
              </p>
              
              {/* Decorative element */}
              <div className={styles.decorativeElement} aria-hidden="true" />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default About;